import useIsMobile from '@/hooks/useIsMobile/useIsMobile';
import * as Tabs from '@radix-ui/react-tabs';
import clsx from 'clsx';
import React, {
  ForwardedRef,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Icon from '../Icon/Icon';
import styles from './RegioTabs.module.scss';
import type {
  RegioTabsContentProps,
  RegioTabsContextProps,
  RegioTabsListProps,
  RegioTabsProps,
  RegioTabsTriggerProps,
} from './types';

const RegioTabsContext = createContext<RegioTabsContextProps>({
  setCurrentValue: () => {},
});

const RegioTabsList = ({
  children,
  className,
  ...props
}: RegioTabsListProps) => {
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLButtonElement[]>([]);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isPrevDisabled, setIsPrevDisabled] = useState(false);
  const isMobile = useIsMobile();

  const updateDisabledStateOfScrollButtons = useCallback(() => {
    setIsNextDisabled(
      tabListRef.current
        ? tabListRef.current?.scrollWidth <=
            Math.ceil(
              tabListRef.current?.scrollLeft +
                tabListRef.current?.getBoundingClientRect().width
            )
        : true
    );
    setIsPrevDisabled(tabListRef.current?.scrollLeft === 0);
  }, []);

  useEffect(() => {
    tabsRef.current = tabsRef.current.slice(0, React.Children.count(children));
  }, [children]);

  const handlePrev = () => {
    tabListRef.current?.scrollBy(-100, 0);
  };

  const handleNext = () => {
    tabListRef.current?.scrollBy(100, 0);
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const listener = (_: Event) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // user scrolling ended
        updateDisabledStateOfScrollButtons();
        if (!isMobile) {
          return;
        }
        tabsRef.current.forEach((el) => {
          if (tabListRef.current) {
            const { x: elX, width: elWidth } = el.getBoundingClientRect();
            const { x: containerX, width: containerWidth } =
              tabListRef.current.getBoundingClientRect();
            const containerXCenter = containerX + containerWidth / 2;
            if (
              // element is positioned crossing center of container
              elX <= containerXCenter &&
              elX + elWidth >= containerXCenter
            ) {
              el.click();
            }
          }
        });
      }, 100);
    };

    const element = tabListRef.current;
    if (element) {
      element.addEventListener('scroll', listener);
      updateDisabledStateOfScrollButtons();
    }
    return () => {
      if (element) {
        element.removeEventListener('scroll', listener);
      }
    };
  }, [updateDisabledStateOfScrollButtons, isMobile]);

  return (
    <div className={styles.listWrapper}>
      <Tabs.List
        className={clsx(styles.list, className)}
        ref={tabListRef}
        {...props}
      >
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement<RegioTabsTriggerProps>(child)) {
            return React.cloneElement(child, {
              ref: (el: HTMLButtonElement | null) => {
                if (el) {
                  tabsRef.current[index] = el;
                }
              },
            });
          }
        })}
      </Tabs.List>
      <button
        type="button"
        aria-label="Wechsle zu vorherigerem Tab"
        className={clsx(styles.scrollButton, styles.prev)}
        onClick={handlePrev}
        disabled={isPrevDisabled}
      >
        <Icon variant="action/chevron-left" size="iconSize24" isOutlined />
      </button>
      <button
        type="button"
        aria-label="Wechsle zu nächstem Tab"
        className={clsx(styles.scrollButton, styles.next)}
        onClick={handleNext}
        disabled={isNextDisabled}
      >
        <Icon variant="action/chevron-right" size="iconSize24" isOutlined />
      </button>
    </div>
  );
};

const RegioTabsTrigger = forwardRef(
  (
    {
      children,
      className,
      iconVariant,
      value,
      ...props
    }: RegioTabsTriggerProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const { setCurrentValue } = useContext(RegioTabsContext);
    return (
      <Tabs.Trigger
        className={clsx(styles.trigger, className)}
        {...props}
        ref={ref}
        value={value}
        onClick={() => {
          setCurrentValue(value);
        }}
      >
        <Icon
          variant={iconVariant}
          size="iconSize64"
          isOutlined
          className={styles.icon}
        />
        {children}
      </Tabs.Trigger>
    );
  }
);

RegioTabsTrigger.displayName = 'RegioTabsTrigger';

const RegioTabsContent = ({
  children,
  className,
  ...props
}: RegioTabsContentProps) => (
  <Tabs.Content className={clsx(styles.content, className)} {...props}>
    <div className={styles.contentInner}>{children}</div>
  </Tabs.Content>
);

const RegioTabs = ({
  children,
  className,
  onValueChange,
  ...props
}: RegioTabsProps) => {
  const [currentValue, setCurrentValue] = useState(props.defaultValue || '');

  useEffect(() => {
    onValueChange?.(currentValue);
  }, [currentValue, onValueChange]);

  return (
    <RegioTabsContext.Provider value={{ setCurrentValue }}>
      <Tabs.Root
        className={clsx(styles.base, className)}
        value={currentValue || undefined}
        {...props}
      >
        {children}
      </Tabs.Root>
    </RegioTabsContext.Provider>
  );
};

RegioTabs.List = RegioTabsList;
RegioTabs.Trigger = RegioTabsTrigger;
RegioTabs.Content = RegioTabsContent;

export default RegioTabs;
